import { dateFr, ecartCycle, finProposee, libelleDuree } from '../lib/cycle-formation';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BoutonMint,
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { Icone } from '../components/Icone';
import { useToast } from '../components/ui/useToast';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import {
  useCreerSession,
  useDetacherApprenant,
  useModifierSession,
  useRattacherApprenant,
  useSessions,
  type DossierAffectable,
  type LigneSession,
  type ReglageRappelSession,
} from '../lib/useSessions';

/** Rendu d'une date à l'heure de La Réunion (les dates arrivent en UTC). */
function dateCourte(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

const CLASSE_CHAMP =
  'w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-codialis-navy';

function message(cause: unknown, defaut: string): string {
  return cause instanceof ErreurApi ? cause.message : defaut;
}

/**
 * Écran « Sessions de formation » du pôle pédagogique (CDC §2.2 et §6.1).
 *
 * C'est le pendant du circuit CERFA : un dossier en mode OF ne signe pas de
 * contrat d'apprentissage, il est rattaché à une session, qui porte les dates
 * et le volume du parcours et débouche sur un certificat de réalisation.
 *
 * Les refus de rattachement — dossier en mode CFA, antenne ou formation qui
 * diverge, session complète — viennent de l'API : la règle n'est pas dupliquée
 * ici, seul son message est affiché.
 */
export function PedSessionsPage(): JSX.Element {
  const { site } = usePerimetre();
  const notifier = useToast();
  const { data, isLoading, error } = useSessions(site);

  const creer = useCreerSession(site);
  const rattacher = useRattacherApprenant(site);
  const detacher = useDetacherApprenant(site);
  const modifier = useModifierSession(site);

  const [echec, setEchec] = useState<string | null>(null);

  const sessions = data?.sessions ?? [];
  const aRattacher = data?.aRattacher ?? [];
  const enCours =
    creer.isPending || rattacher.isPending || detacher.isPending || modifier.isPending;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Sessions de formation"
        sousTitre="Pôle pédagogique · mode OF · une session porte les dates du parcours et débouche sur un certificat de réalisation"
        actions={
          <Link
            to="/parcours"
            className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
          >
            Voir le parcours
          </Link>
        }
      />

      <BarreSites />

      {echec && <MessageErreur message={echec} />}
      {error && <MessageErreur message={message(error, 'Chargement impossible.')} />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Compteur
          libelle="Sessions ouvertes"
          valeur={data?.compteurs.sessions ?? '—'}
          detail="dans le périmètre affiché"
          ton="navy"
        />
        <Compteur
          libelle="Dossiers rattachés"
          valeur={data?.compteurs.apprenantsRattaches ?? '—'}
          detail="apprenants suivis en session"
          ton="mint"
        />
        <Compteur
          libelle="À rattacher"
          valeur={data?.compteurs.dossiersARattacher ?? '—'}
          detail="dossiers OF sans session"
          ton={data && data.compteurs.dossiersARattacher > 0 ? 'warm' : 'blue'}
        />
      </div>

      <FormulaireSession
        formations={data?.formations ?? []}
        sites={data?.sites ?? []}
        enCours={creer.isPending}
        erreur={creer.error ? message(creer.error, 'La création a échoué.') : null}
        onCreer={(entree) =>
          creer.mutate(entree, {
            onSuccess: (session) => notifier(`Session « ${session.libelle} » ouverte.`),
          })
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2 min-w-0">
          {isLoading ? (
            <Carte titre="Sessions" sansBordure>
              <Chargement libelle="Chargement des sessions…" />
            </Carte>
          ) : sessions.length === 0 ? (
            <Carte titre="Sessions" sansBordure>
              <p className="p-4 sm:p-6 text-sm text-codialis-gray">
                Aucune session dans ce périmètre. Un dossier en mode OF reste sans parcours
                jusqu’à son rattachement à une session.
              </p>
            </Carte>
          ) : (
            sessions.map((session) => (
              <CarteSession
                key={session.id}
                session={session}
                candidats={aRattacher}
                enCours={enCours}
                onRattacher={(apprenantId) => {
                  setEchec(null);
                  rattacher.mutate(
                    { sessionId: session.id, apprenantId },
                    {
                      onSuccess: () => notifier('Dossier rattaché à la session.'),
                      onError: (cause) =>
                        setEchec(message(cause, 'Le rattachement n’a pas abouti.')),
                    },
                  );
                }}
                onDetacher={(apprenantId) => {
                  setEchec(null);
                  detacher.mutate(
                    { sessionId: session.id, apprenantId },
                    {
                      onSuccess: () => notifier('Dossier détaché de la session.'),
                      onError: (cause) =>
                        setEchec(message(cause, 'Le détachement n’a pas abouti.')),
                    },
                  );
                }}
                onCode={(codeEmargement) => {
                  setEchec(null);
                  modifier.mutate(
                    { id: session.id, codeEmargement },
                    {
                      onSuccess: () =>
                        notifier(
                          codeEmargement === null
                            ? 'Code de la session retiré : celui de la formation s’applique.'
                            : 'Code d’émargement de la session enregistré.',
                        ),
                      onError: (cause) =>
                        setEchec(message(cause, 'La modification n’a pas abouti.')),
                    },
                  );
                }}
                onRappel={(rappelEmargement) => {
                  setEchec(null);
                  modifier.mutate(
                    { id: session.id, rappelEmargement },
                    {
                      onSuccess: () => notifier('Rappels d’émargement de la session enregistrés.'),
                      onError: (cause) =>
                        setEchec(message(cause, 'La modification n’a pas abouti.')),
                    },
                  );
                }}
                onPlaces={(placesMax) => {
                  setEchec(null);
                  modifier.mutate(
                    { id: session.id, placesMax },
                    {
                      onSuccess: () => notifier('Nombre de places mis à jour.'),
                      onError: (cause) =>
                        setEchec(message(cause, 'La modification n’a pas abouti.')),
                    },
                  );
                }}
              />
            ))
          )}
        </div>

        <div className="space-y-6">
          <Carte
            titre="Dossiers OF à rattacher"
            actions={<span className="text-xs text-codialis-gray">{aRattacher.length}</span>}
            sansBordure
          >
            {aRattacher.length === 0 ? (
              <p className="p-4 sm:p-6 text-sm text-codialis-gray">
                Tous les dossiers en mode OF du périmètre sont rattachés à une session.
              </p>
            ) : (
              <div className="divide-y divide-gray-100">
                {aRattacher.map((dossier) => (
                  <div key={dossier.id} className="flex items-start gap-3 px-4 sm:px-6 py-3">
                    <Icone nom="dossier" className="mt-0.5 h-4 w-4 shrink-0 text-codialis-sky" />
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/adm-dossier/${dossier.id}`}
                        className="block truncate text-sm font-semibold text-codialis-navy hover:underline"
                      >
                        {dossier.prenom} {dossier.nom}
                      </Link>
                      <div className="truncate text-[11px] text-codialis-gray">
                        {dossier.formation ?? 'Formation à choisir'} · antenne {dossier.site}
                      </div>
                    </div>
                    <Pastille texte={dossier.statutLibelle} ton={dossier.statutTon} />
                  </div>
                ))}
              </div>
            )}
          </Carte>

          <div className="rounded-2xl bg-white p-4 sm:p-6 shadow-card">
            <div className="text-[11px] font-bold uppercase tracking-widest text-codialis-teal">
              Ce que le mode change
            </div>
            <h2 className="font-display mt-1 text-lg font-bold text-codialis-navy">
              Pas de CERFA en mode OF
            </h2>
            <p className="mt-1 text-sm text-codialis-gray">
              Un dossier en mode CFA passe par un contrat d’apprentissage et un CERFA ; il ne
              se rattache pas à une session. Un dossier en mode OF suit une session, et son
              certificat de réalisation atteste les heures constatées par émargement.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Champ({
  libelle,
  children,
  groupe = false,
}: {
  libelle: string;
  children: React.ReactNode;
  /** Plusieurs commandes (boutons, choix) : un `fieldset`, le libellé en légende. */
  groupe?: boolean;
}): JSX.Element {
  // Le libellé enveloppe son champ : il le nomme sans identifiant à poser
  // (RGAA 11.1, TF-341).
  if (groupe) {
    return (
      <fieldset>
        <legend className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
          {libelle}
        </legend>
        {children}
      </fieldset>
    );
  }
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
      </span>
      {children}
    </label>
  );
}

function FormulaireSession({
  formations,
  sites,
  enCours,
  erreur,
  onCreer,
}: {
  formations: { code: string; titre: string; niveau: string; dureeAnnees?: number | null }[];
  sites: string[];
  enCours: boolean;
  erreur: string | null;
  onCreer: (entree: {
    formationCode: string;
    siteCode: string;
    libelle: string;
    dateDebut: string;
    dateFin: string;
    placesMax?: number;
  }) => void;
}): JSX.Element {
  const [formationCode, setFormationCode] = useState('');
  const [siteCode, setSiteCode] = useState('');
  const [libelle, setLibelle] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [placesMax, setPlacesMax] = useState('');
  // La fin suit la durée de la formation tant qu'on ne l'a pas corrigée.
  const [finTouchee, setFinTouchee] = useState(false);
  const duree = formations.find((entree) => entree.code === formationCode)?.dureeAnnees ?? null;
  const proposerFin = (debut: string, dureeAnnees: number | null): void => {
    if (finTouchee || !debut || !dureeAnnees) return;
    const fin = finProposee(debut, dureeAnnees);
    if (fin) setDateFin(fin);
  };

  // Une seule antenne dans le périmètre : elle est choisie d'office, il n'y a
  // rien à sélectionner (CDC §2.3).
  const siteRetenu = sites.length === 1 ? (sites[0] ?? '') : siteCode;
  const datesCoherentes = Boolean(dateDebut && dateFin) && dateFin >= dateDebut;
  const complet =
    Boolean(formationCode && siteRetenu && libelle.trim().length >= 3) && datesCoherentes;

  return (
    <Carte titre="Ouvrir une session de formation" sansBordure>
      <form
        className="grid grid-cols-1 gap-4 p-4 sm:p-6 md:grid-cols-2 xl:grid-cols-3"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          if (!complet || enCours) return;
          const places = Number(placesMax);
          onCreer({
            formationCode,
            siteCode: siteRetenu,
            libelle: libelle.trim(),
            dateDebut,
            dateFin,
            ...(placesMax && Number.isFinite(places) && places > 0
              ? { placesMax: places }
              : {}),
          });
          setLibelle('');
          setPlacesMax('');
        }}
      >
        <Champ libelle="Formation">
          <select
            value={formationCode}
            onChange={(evenement) => {
              const code = evenement.target.value;
              setFormationCode(code);
              // Le libellé est proposé, pas imposé : la session porte souvent
              // le nom d'une promotion.
              const formation = formations.find((entree) => entree.code === code);
              proposerFin(dateDebut, formation?.dureeAnnees ?? null);
              if (formation && !libelle.trim()) {
                setLibelle(`${formation.titre} · session ${new Date().getFullYear()}`);
              }
            }}
            className={CLASSE_CHAMP}
          >
            <option value="">Choisir…</option>
            {formations.map((formation) => (
              <option key={formation.code} value={formation.code}>
                {formation.titre} ({formation.code})
              </option>
            ))}
          </select>
        </Champ>

        <Champ libelle="Antenne">
          {sites.length === 1 ? (
            <div className={`${CLASSE_CHAMP} font-semibold`}>{siteRetenu}</div>
          ) : (
            <select
              value={siteCode}
              onChange={(evenement) => setSiteCode(evenement.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">Choisir…</option>
              {sites.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          )}
        </Champ>

        <Champ libelle="Intitulé de la session">
          <input
            value={libelle}
            onChange={(evenement) => setLibelle(evenement.target.value)}
            placeholder="Assistant commercial · session automne"
            className={CLASSE_CHAMP}
          />
        </Champ>

        <Champ libelle="Début">
          <input
            type="date"
            value={dateDebut}
            onChange={(evenement) => {
              setDateDebut(evenement.target.value);
              proposerFin(evenement.target.value, duree);
            }}
            className={CLASSE_CHAMP}
          />
        </Champ>

        <Champ libelle="Fin">
          <input
            type="date"
            value={dateFin}
            min={dateDebut || undefined}
            onChange={(evenement) => {
              setFinTouchee(true);
              setDateFin(evenement.target.value);
            }}
            className={CLASSE_CHAMP}
          />
        </Champ>

        <Champ libelle="Places (facultatif)">
          <input
            type="number"
            min={1}
            max={500}
            value={placesMax}
            onChange={(evenement) => setPlacesMax(evenement.target.value)}
            placeholder="Sans limite"
            className={CLASSE_CHAMP}
          />
        </Champ>

        <div className="space-y-3 md:col-span-2 xl:col-span-3 min-w-0">
          {erreur && <MessageErreur message={erreur} />}
          {datesCoherentes && (
            <p className="text-sm text-codialis-navy" aria-live="polite">
              <span className="font-semibold">
                Durée : {duree ? libelleDuree(duree) : 'non renseignée sur la formation'}
              </span>{' '}
              · Du {dateFr(dateDebut)} au {dateFr(dateFin)}
              {!finTouchee && duree ? ' · fin proposée par la durée de la formation' : ''}
            </p>
          )}
          {datesCoherentes && ecartCycle(duree, dateDebut, dateFin) && (
            <p className="text-xs font-semibold text-amber-800">
              {ecartCycle(duree, dateDebut, dateFin)} Vérifiez la date de fin.
            </p>
          )}
          {dateDebut && dateFin && !datesCoherentes && (
            <p className="text-xs font-semibold text-red-600">
              La date de fin ne peut pas précéder la date de début.
            </p>
          )}
          <BoutonPrincipal type="submit" disabled={!complet || enCours}>
            {enCours ? 'Ouverture…' : 'Ouvrir la session'}
          </BoutonPrincipal>
        </div>
      </form>
    </Carte>
  );
}

function CarteSession({
  session,
  candidats,
  enCours,
  onRattacher,
  onDetacher,
  onPlaces,
  onCode,
  onRappel,
}: {
  session: LigneSession;
  candidats: DossierAffectable[];
  enCours: boolean;
  onRattacher: (apprenantId: string) => void;
  onDetacher: (apprenantId: string) => void;
  onPlaces: (placesMax: number | null) => void;
  /** Code d'émargement propre à la session ; `null` rend la main à la formation. */
  onCode: (code: string | null) => void;
  onRappel: (rappel: ReglageRappelSession) => void;
}): JSX.Element {
  const [choix, setChoix] = useState('');
  const [code, setCode] = useState('');
  const [places, setPlaces] = useState(
    session.placesMax === null ? '' : String(session.placesMax),
  );

  // Seuls les dossiers de l'antenne de la session peuvent la rejoindre : les
  // proposer tous ferait cliquer pour un refus (CDC §2.3).
  const eligibles = candidats.filter(
    (dossier) =>
      dossier.site === session.site.code &&
      (dossier.formationCode === null || dossier.formationCode === session.formation.code),
  );
  const complete = session.placesRestantes === 0;

  return (
    <Carte
      titre={session.libelle}
      actions={
        <>
          <Pastille texte={session.site.code} ton="info" />
          <Pastille
            texte={
              session.placesRestantes === null
                ? `${session.effectif} inscrit(s)`
                : `${session.effectif}/${session.placesMax} place(s)`
            }
            ton={complete ? 'warning' : 'mint'}
          />
        </>
      }
      sansBordure
    >
      <div className="space-y-4 p-4 sm:p-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Renseignement libelle="Formation" valeur={session.formation.titre} />
          <Renseignement
            libelle="Période"
            valeur={`${dateCourte(session.dateDebut)} → ${dateCourte(session.dateFin)}`}
          />
          <Renseignement libelle="Volume au programme" valeur={`${session.formation.heures} h`} />
          <Renseignement
            libelle="Séances planifiées"
            valeur={session.seances === 0 ? 'Aucune' : String(session.seances)}
          />
        </div>

        <div className="rounded-xl bg-codialis-sand p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            Apprenants rattachés
          </div>
          {session.apprenants.length === 0 ? (
            <p className="mt-1 text-sm text-codialis-gray">
              Aucun dossier rattaché à cette session.
            </p>
          ) : (
            <ul className="mt-2 space-y-2">
              {session.apprenants.map((apprenant) => (
                <li
                  key={apprenant.id}
                  className="flex flex-wrap items-center gap-2 rounded-lg bg-white px-3 py-2"
                >
                  <Link
                    to={`/adm-dossier/${apprenant.id}`}
                    className="min-w-0 flex-1 truncate text-sm font-semibold text-codialis-navy hover:underline"
                  >
                    {apprenant.prenom} {apprenant.nom}
                  </Link>
                  <Pastille texte={apprenant.statutLibelle} ton={apprenant.statutTon} />
                  <button
                    type="button"
                    disabled={enCours}
                    onClick={() => onDetacher(apprenant.id)}
                    className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-codialis-gray transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Détacher
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[200px] flex-1">
              <Champ libelle="Rattacher un dossier OF">
                <select
                  value={choix}
                  disabled={enCours || complete || eligibles.length === 0}
                  onChange={(evenement) => setChoix(evenement.target.value)}
                  className={`${CLASSE_CHAMP} disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <option value="">
                    {complete
                      ? 'Session complète'
                      : eligibles.length === 0
                        ? 'Aucun dossier éligible'
                        : 'Choisir un dossier…'}
                  </option>
                  {eligibles.map((dossier) => (
                    <option key={dossier.id} value={dossier.id}>
                      {dossier.prenom} {dossier.nom}
                    </option>
                  ))}
                </select>
              </Champ>
            </div>
            <BoutonMint
              disabled={!choix || enCours || complete}
              onClick={() => {
                if (!choix) return;
                onRattacher(choix);
                setChoix('');
              }}
            >
              Rattacher
            </BoutonMint>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[140px] flex-1">
              <Champ libelle="Places">
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={places}
                  onChange={(evenement) => setPlaces(evenement.target.value)}
                  placeholder="Sans limite"
                  className={CLASSE_CHAMP}
                />
              </Champ>
            </div>
            <BoutonSecondaire
              disabled={enCours || places === (session.placesMax === null ? '' : String(session.placesMax))}
              onClick={() => {
                const valeur = Number(places);
                onPlaces(places === '' || !Number.isFinite(valeur) ? null : valeur);
              }}
            >
              Enregistrer
            </BoutonSecondaire>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[160px] flex-1">
            <Champ libelle="Code d’émargement de la session">
              <input
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                maxLength={4}
                value={code}
                onChange={(evenement) => setCode(evenement.target.value.replace(/\D/g, ''))}
                placeholder={session.codeEmargement.source === 'SESSION' ? '••••' : '0000'}
                className={`${CLASSE_CHAMP} tracking-[0.4em]`}
              />
            </Champ>
          </div>
          <BoutonSecondaire
            disabled={enCours || code.length !== 4}
            onClick={() => {
              onCode(code);
              setCode('');
            }}
          >
            {session.codeEmargement.source === 'SESSION' ? 'Remplacer' : 'Définir'}
          </BoutonSecondaire>
          {session.codeEmargement.source === 'SESSION' && (
            <BoutonSecondaire disabled={enCours} onClick={() => onCode(null)}>
              Retirer
            </BoutonSecondaire>
          )}
          <p className="w-full text-[11px] text-codialis-gray">
            {session.codeEmargement.source === 'SESSION'
              ? 'Code propre à cette session : il l’emporte sur celui de la formation. Il n’est jamais réaffiché.'
              : session.codeEmargement.source === 'FORMATION'
                ? 'La session utilise le code de sa formation. En définir un ici le remplace pour ce groupe seulement.'
                : 'Aucun code paramétré : le formateur en choisit un à chaque appel.'}
          </p>
        </div>

        <RappelsSession session={session} enCours={enCours} onRappel={onRappel} />

        <p className="text-[11px] text-codialis-gray">
          Le certificat de réalisation se génère depuis les documents réglementaires du
          dossier, et n’atteste que les heures constatées par émargement.
        </p>
      </div>
    </Carte>
  );
}

type ModeRappel = 'ORGANISME' | 'PROPRE' | 'AUCUN';

function modeRappel(rappel: ReglageRappelSession): ModeRappel {
  if (!rappel.actif) return 'AUCUN';
  return rappel.heureMatin === null && rappel.heureApresMidi === null ? 'ORGANISME' : 'PROPRE';
}

/**
 * Rappels d'émargement de la session (TF-460) : les heures de l'organisme, les
 * siennes, ou aucun rappel. Les plages sont jugées par l'API.
 */
function RappelsSession({
  session,
  enCours,
  onRappel,
}: {
  session: LigneSession;
  enCours: boolean;
  onRappel: (rappel: ReglageRappelSession) => void;
}): JSX.Element {
  const rappel = session.rappelEmargement;
  const [mode, setMode] = useState<ModeRappel>(modeRappel(rappel));
  const [matin, setMatin] = useState(rappel.heureMatin ?? rappel.organisme.matin);
  const [apresMidi, setApresMidi] = useState(rappel.heureApresMidi ?? rappel.organisme.apresMidi);
  useEffect(() => {
    setMode(modeRappel(rappel));
    setMatin(rappel.heureMatin ?? rappel.organisme.matin);
    setApresMidi(rappel.heureApresMidi ?? rappel.organisme.apresMidi);
  }, [rappel]);

  const voulu: ReglageRappelSession =
    mode === 'AUCUN'
      ? { actif: false, heureMatin: rappel.heureMatin, heureApresMidi: rappel.heureApresMidi }
      : mode === 'ORGANISME'
        ? { actif: true, heureMatin: null, heureApresMidi: null }
        : { actif: true, heureMatin: matin, heureApresMidi: apresMidi };
  const inchange =
    voulu.actif === rappel.actif &&
    voulu.heureMatin === rappel.heureMatin &&
    voulu.heureApresMidi === rappel.heureApresMidi;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[200px] flex-1">
          <Champ libelle="Rappels d’émargement">
            <select
              value={mode}
              aria-label="Rappels d’émargement de la session"
              disabled={enCours}
              onChange={(evenement) => setMode(evenement.target.value as ModeRappel)}
              className={CLASSE_CHAMP}
            >
              <option value="ORGANISME">
                Heures de l’organisme ({rappel.organisme.matin} · {rappel.organisme.apresMidi})
              </option>
              <option value="PROPRE">Heures propres à la session</option>
              <option value="AUCUN">Aucun rappel</option>
            </select>
          </Champ>
        </div>
        {mode === 'PROPRE' && (
          <>
            <Champ libelle="Matin">
              <input
                type="time"
                required
                aria-label="Heure du rappel du matin"
                value={matin}
                onChange={(evenement) => setMatin(evenement.target.value)}
                className={`${CLASSE_CHAMP} w-32`}
              />
            </Champ>
            <Champ libelle="Après-midi">
              <input
                type="time"
                required
                aria-label="Heure du rappel de l’après-midi"
                value={apresMidi}
                onChange={(evenement) => setApresMidi(evenement.target.value)}
                className={`${CLASSE_CHAMP} w-32`}
              />
            </Champ>
          </>
        )}
        <BoutonSecondaire
          disabled={enCours || inchange || (mode === 'PROPRE' && (!matin || !apresMidi))}
          onClick={() => onRappel(voulu)}
        >
          Enregistrer
        </BoutonSecondaire>
      </div>
      <p className="text-[11px] text-codialis-gray">
        {rappel.applique.matin && rappel.applique.apresMidi
          ? `Les apprenants qui n’ont pas encore émargé reçoivent un rappel à ${rappel.applique.matin} et à ${rappel.applique.apresMidi} (e-mail, et notification sur leur téléphone s’ils l’ont activée).`
          : 'Aucun rappel d’émargement pour cette session.'}
      </p>
    </div>
  );
}

function Renseignement({ libelle, valeur }: { libelle: string; valeur: string }): JSX.Element {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
        {libelle}
      </div>
      <div className="mt-0.5 text-sm font-semibold text-codialis-navy">{valeur}</div>
    </div>
  );
}
